/**
 * Brand palette definitions.
 * All colour values are [R, G, B] tuples in 0–255 integer range.
 *
 * Built from 4 brand anchors:
 *   teal    #27beb4  [ 39, 190, 180]
 *   magenta #ff009e  [255,   0, 158]
 *   coral   #ff6357  [255,  99,  87]
 *   sky     #54d3fe  [ 84, 211, 254]
 *
 * The cyan / violet / magenta slots feed the standard 3-stop headline
 * gradient (cyan 0% → violet 50% → magenta 100%). Each theme reorders
 * those slots to tell a different story (cool→hot, hot→cool, duotone, etc).
 *
 * @typedef {readonly [number, number, number]} RgbTuple
 *
 * @typedef {{
 *   name:         string,
 *   cyan:         RgbTuple,
 *   violet:       RgbTuple,
 *   magenta:      RgbTuple,
 *   teal:         RgbTuple,
 *   plumDeep:     RgbTuple,
 *   electric:     RgbTuple,
 *   purpleMid:    RgbTuple,
 *   ice:          RgbTuple,
 *   particleStar: RgbTuple,
 *   particleLine: RgbTuple,
 *   surfaceDeep0: RgbTuple,
 *   surfaceDeep1: RgbTuple,
 *   dataUp:       RgbTuple,
 *   dataDown:     RgbTuple,
 * }} BrandPalette
 */

export const BRAND_PALETTES = {

  /* Pre-theming production palette — boot default so the homepage looks the
     same as it did before BrandThemeProvider was introduced. The 10 entries
     after this are brand-anchored variations the picker offers. */
  original: {
    name: 'Original',
    cyan:         [ 61, 214, 255],
    violet:       [123,  97, 255],
    magenta:      [168,  85, 247],
    teal:         [ 52, 211, 153],
    plumDeep:     [ 88,  41, 163],
    electric:     [ 80, 195, 255],
    purpleMid:    [108,  92, 231],
    ice:          [125, 234, 255],
    particleStar: [165, 148, 255],
    particleLine: [140, 110, 255],
    surfaceDeep0: [  8,   9,  13],
    surfaceDeep1: [ 11,  13,  20],
    dataUp:       [ 61, 214, 255],
    dataDown:     [255, 100, 110],
  },

  tealSignal: {
    name: 'Teal Signal',
    cyan:         [ 39, 190, 180],
    violet:       [ 84, 211, 254],
    magenta:      [255,   0, 158],
    teal:         [ 80, 220, 210],
    plumDeep:     [110,   0,  70],
    electric:     [120, 235, 225],
    purpleMid:    [120, 160, 230],
    ice:          [195, 245, 255],
    particleStar: [150, 235, 230],
    particleLine: [ 80, 200, 230],
    surfaceDeep0: [  4,  10,  14],
    surfaceDeep1: [  7,  14,  20],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  skyForward: {
    name: 'Sky Forward',
    cyan:         [ 84, 211, 254],
    violet:       [ 39, 190, 180],
    magenta:      [255,   0, 158],
    teal:         [120, 230, 210],
    plumDeep:     [110,   0,  70],
    electric:     [140, 230, 255],
    purpleMid:    [120, 200, 230],
    ice:          [200, 240, 255],
    particleStar: [180, 230, 255],
    particleLine: [100, 190, 240],
    surfaceDeep0: [  3,   8,  16],
    surfaceDeep1: [  6,  12,  22],
    dataUp:       [120, 230, 200],
    dataDown:     [255, 100, 110],
  },

  magentaPulse: {
    name: 'Magenta Pulse',
    cyan:         [255,   0, 158],
    violet:       [ 84, 211, 254],
    magenta:      [ 39, 190, 180],
    teal:         [255,  80, 180],
    plumDeep:     [ 80,   0,  50],
    electric:     [255,  90, 200],
    purpleMid:    [200, 100, 220],
    ice:          [255, 200, 235],
    particleStar: [255, 150, 220],
    particleLine: [220,  60, 180],
    surfaceDeep0: [ 12,   4,  12],
    surfaceDeep1: [ 18,   6,  18],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  coralHeat: {
    name: 'Coral Heat',
    cyan:         [255,  99,  87],
    violet:       [255,   0, 158],
    magenta:      [ 39, 190, 180],
    teal:         [255, 130, 110],
    plumDeep:     [ 80,  10,  30],
    electric:     [255, 140, 120],
    purpleMid:    [255,  80, 130],
    ice:          [255, 220, 200],
    particleStar: [255, 180, 160],
    particleLine: [230,  80,  90],
    surfaceDeep0: [ 12,   5,   5],
    surfaceDeep1: [ 18,   7,   8],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255,  80,  80],
  },

  tide: {
    name: 'Tide',
    cyan:         [ 39, 190, 180],
    violet:       [ 60, 200, 220],
    magenta:      [ 84, 211, 254],
    teal:         [100, 230, 220],
    plumDeep:     [ 10,  50,  90],
    electric:     [110, 235, 240],
    purpleMid:    [ 80, 180, 230],
    ice:          [200, 245, 255],
    particleStar: [160, 235, 250],
    particleLine: [ 80, 190, 230],
    surfaceDeep0: [  3,   9,  16],
    surfaceDeep1: [  5,  13,  22],
    dataUp:       [100, 240, 200],
    dataDown:     [255, 100, 110],
  },

  neonBloom: {
    name: 'Neon Bloom',
    cyan:         [255,   0, 158],
    violet:       [255,  50, 130],
    magenta:      [255,  99,  87],
    teal:         [255, 100, 180],
    plumDeep:     [ 80,   0,  40],
    electric:     [255, 120, 180],
    purpleMid:    [255,  80, 140],
    ice:          [255, 210, 220],
    particleStar: [255, 170, 190],
    particleLine: [240,  60, 130],
    surfaceDeep0: [ 12,   4,   8],
    surfaceDeep1: [ 18,   6,  12],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255,  80, 100],
  },

  cyberSunset: {
    name: 'Cyber Sunset',
    cyan:         [255,  99,  87],
    violet:       [ 84, 211, 254],
    magenta:      [255,   0, 158],
    teal:         [255, 130, 110],
    plumDeep:     [ 60,   0,  50],
    electric:     [255, 140, 120],
    purpleMid:    [180, 130, 220],
    ice:          [255, 230, 230],
    particleStar: [255, 180, 200],
    particleLine: [200, 100, 180],
    surfaceDeep0: [ 10,   5,  12],
    surfaceDeep1: [ 15,   8,  18],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255,  90,  80],
  },

  aurora: {
    name: 'Aurora',
    cyan:         [ 39, 190, 180],
    violet:       [255,   0, 158],
    magenta:      [ 84, 211, 254],
    teal:         [ 80, 220, 200],
    plumDeep:     [ 80,   0,  60],
    electric:     [110, 235, 220],
    purpleMid:    [200, 100, 200],
    ice:          [200, 245, 255],
    particleStar: [180, 220, 255],
    particleLine: [100, 200, 240],
    surfaceDeep0: [  4,   8,  14],
    surfaceDeep1: [  7,  12,  20],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  reef: {
    name: 'Reef',
    cyan:         [ 84, 211, 254],
    violet:       [ 39, 190, 180],
    magenta:      [255,  99,  87],
    teal:         [120, 230, 220],
    plumDeep:     [ 40,  20,  50],
    electric:     [140, 230, 255],
    purpleMid:    [120, 200, 200],
    ice:          [220, 245, 255],
    particleStar: [180, 235, 240],
    particleLine: [100, 200, 230],
    surfaceDeep0: [  4,  10,  16],
    surfaceDeep1: [  7,  14,  22],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100,  90],
  },

  plasma: {
    name: 'Plasma',
    cyan:         [255,   0, 158],
    violet:       [255,  99,  87],
    magenta:      [ 84, 211, 254],
    teal:         [255,  80, 180],
    plumDeep:     [ 80,   0,  50],
    electric:     [255,  90, 180],
    purpleMid:    [255, 130, 130],
    ice:          [255, 220, 220],
    particleStar: [255, 170, 200],
    particleLine: [240, 100, 150],
    surfaceDeep0: [ 12,   4,  10],
    surfaceDeep1: [ 18,   7,  16],
    dataUp:       [ 80, 230, 200],
    dataDown:     [255,  90,  80],
  },

  /* ─── Set 2 — cool-leaning variations: violet in Velvet/Iris (only),
     gray #3c3c3c in Slate Cyan/Carbon Pop (only), no orange. ─── */

  lagoon: {
    name: 'Lagoon',
    cyan:         [ 39, 190, 180],
    violet:       [ 60, 205, 220],
    magenta:      [ 84, 211, 254],
    teal:         [ 80, 220, 200],
    plumDeep:     [ 10,  50,  90],
    electric:     [110, 235, 240],
    purpleMid:    [ 70, 205, 230],
    ice:          [200, 245, 255],
    particleStar: [160, 235, 250],
    particleLine: [ 80, 200, 230],
    surfaceDeep0: [  3,   9,  16],
    surfaceDeep1: [  5,  13,  22],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  lagoon2: {
    name: 'Lagoon 2',
    /* Lagoon with the bright spring-green mint promoted into the
       headline gradient and chrome. Headline sweep: teal → mint →
       sky. Magenta anchor sits in the deep-surface plum slot as a
       quiet undertone so all four non-coral brand colors appear. */
    cyan:         [ 39, 190, 180],   // teal anchor (gradient start)
    violet:       [ 80, 230, 180],   // mint anchor (gradient mid — promoted)
    magenta:      [ 84, 211, 254],   // sky anchor (gradient end)
    teal:         [110, 235, 195],   // bright mint-teal
    plumDeep:     [255,   0, 158],   // magenta anchor (deep undertone)
    electric:     [120, 245, 210],
    purpleMid:    [ 90, 220, 195],
    ice:          [210, 250, 235],
    particleStar: [180, 245, 215],   // mint-tinted particles
    particleLine: [ 80, 220, 195],
    surfaceDeep0: [  3,  10,  16],
    surfaceDeep1: [  5,  14,  22],
    dataUp:       [ 80, 230, 180],   // mint
    dataDown:     [255, 100, 110],
  },

  /* ─── Lagoon-inspired trio (all 5 official brand anchors:
        magenta 255,0,158 · coral 255,99,87 · teal 39,190,180 ·
        mint 80,230,180 · sky 84,211,254). Each theme assigns
        every anchor to a meaningful slot, then surrounds them
        with bridging tones drawn from the same family. ─── */

  meridian: {
    name: 'Meridian',
    /* High-noon bright lagoon — sky → mint → coral horizon, with
       the magenta anchor as a deep undercurrent surface tint. */
    cyan:         [ 84, 211, 254],   // sky (gradient start)
    violet:       [ 80, 230, 180],   // mint (gradient mid)
    magenta:      [255,  99,  87],   // coral (gradient end — warm horizon)
    teal:         [ 39, 190, 180],   // teal (cool secondary)
    plumDeep:     [255,   0, 158],   // magenta (deep accent / undercurrent)
    electric:     [130, 240, 215],
    purpleMid:    [110, 220, 220],
    ice:          [210, 248, 235],
    particleStar: [170, 240, 220],
    particleLine: [100, 215, 230],
    surfaceDeep0: [  4,  14,  20],
    surfaceDeep1: [  6,  18,  26],
    dataUp:       [ 80, 230, 180],   // mint
    dataDown:     [255,  99,  87],   // coral
  },

  ebbtide: {
    name: 'Ebbtide',
    /* Sunset on still water — coral → magenta → sky reflection,
       with the teal anchor as the deep water underneath and mint
       as the bright wave-crest accent. */
    cyan:         [255,  99,  87],   // coral (warm gradient start)
    violet:       [255,   0, 158],   // magenta (gradient mid)
    magenta:      [ 84, 211, 254],   // sky (cool gradient end — reflection)
    teal:         [ 80, 230, 180],   // mint (wave-crest accent)
    plumDeep:     [ 39, 190, 180],   // teal (deep water undertone)
    electric:     [255, 140, 110],
    purpleMid:    [220,  90, 200],
    ice:          [255, 224, 220],
    particleStar: [255, 170, 160],
    particleLine: [220, 100, 200],
    surfaceDeep0: [ 14,   8,  18],
    surfaceDeep1: [ 18,  10,  22],
    dataUp:       [ 80, 230, 180],   // mint
    dataDown:     [255,  90,  90],
  },

  tidepool: {
    name: 'Tide Pool',
    /* Kaleidoscopic — all five anchors live at near-equal weight
       around a cool teal/mint base. The most colorful of the trio
       while still reading as a lagoon-family theme. */
    cyan:         [ 39, 190, 180],   // teal (gradient start)
    violet:       [ 80, 230, 180],   // mint (bright bridge)
    magenta:      [255,   0, 158],   // magenta (vivid gradient end)
    teal:         [255,  99,  87],   // coral (bright accent slot)
    plumDeep:     [ 50,  18,  70],   // deep plum
    electric:     [ 84, 211, 254],   // sky (highlight)
    purpleMid:    [180,  90, 200],   // pink-purple bridge
    ice:          [220, 245, 235],
    particleStar: [ 84, 211, 254],   // sky particles
    particleLine: [ 80, 230, 180],   // mint traces
    surfaceDeep0: [  5,  12,  16],
    surfaceDeep1: [  8,  16,  22],
    dataUp:       [ 80, 230, 180],   // mint
    dataDown:     [255,  99,  87],   // coral
  },

  /* ─── Blue-Greens family ─────────────────────────────────
        Lagoon-inspired but coral-free. Each theme uses every
        non-coral brand anchor — teal (39,190,180), mint
        (80,230,180), sky (84,211,254) and magenta (255,0,158) —
        but the dominant character stays in the blue-green zone.
        Magenta is always a deep / sparse accent (plumDeep, deep
        surface tint, or one bioluminescent flash) so the warm
        anchor doesn't pull the theme out of the lagoon family. */

  deepReef: {
    name: 'Deep Reef',
    /* Deep teal water dominant; mint sits in the bright accent
       slot, sky closes out the headline gradient, magenta lives
       in the deep-surface plum slot as a quiet undertow. */
    cyan:         [ 39, 190, 180],   // teal anchor (gradient start)
    violet:       [ 75, 210, 195],
    magenta:      [ 84, 211, 254],   // sky anchor (gradient end)
    teal:         [ 80, 230, 180],   // mint anchor (bright accent)
    plumDeep:     [255,   0, 158],   // magenta anchor (deep undertow)
    electric:     [120, 240, 220],
    purpleMid:    [ 60, 175, 175],
    ice:          [195, 240, 230],
    particleStar: [155, 230, 215],
    particleLine: [ 70, 195, 195],
    surfaceDeep0: [  2,   8,  14],
    surfaceDeep1: [  4,  12,  20],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  shoal: {
    name: 'Shoal',
    /* Pale, glassy shallow water. Headline gradient sweeps mint
       through sky, with the teal and magenta anchors anchoring
       the secondary slot and the deep surface accent. */
    cyan:         [ 80, 230, 180],   // mint anchor (gradient start)
    violet:       [110, 220, 215],   // pale teal-aqua bridge
    magenta:      [ 84, 211, 254],   // sky anchor (gradient end)
    teal:         [ 39, 190, 180],   // teal anchor (secondary)
    plumDeep:     [255,   0, 158],   // magenta anchor (deep accent)
    electric:     [180, 245, 230],
    purpleMid:    [135, 220, 230],
    ice:          [220, 250, 245],
    particleStar: [200, 245, 240],
    particleLine: [125, 215, 215],
    surfaceDeep0: [  4,  12,  18],
    surfaceDeep1: [  7,  16,  24],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  kelpForest: {
    name: 'Kelp Forest',
    /* Mint-led green-dominant; teal closes the headline gradient,
       sky lives in the bright accent slot, magenta is the rare
       deep-water bloom in the plum slot. */
    cyan:         [ 80, 230, 180],   // mint anchor (gradient start)
    violet:       [110, 220, 175],
    magenta:      [ 39, 190, 180],   // teal anchor (gradient end)
    teal:         [ 84, 211, 254],   // sky anchor (bright accent)
    plumDeep:     [255,   0, 158],   // magenta anchor (deep bloom)
    electric:     [120, 245, 195],
    purpleMid:    [ 70, 195, 175],
    ice:          [200, 245, 220],
    particleStar: [160, 240, 200],
    particleLine: [ 70, 210, 170],
    surfaceDeep0: [  3,  10,   8],
    surfaceDeep1: [  5,  14,  12],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  glacier: {
    name: 'Glacier',
    /* Sky-led crisp. Ice highlights, teal anchors the secondary
       slot, mint closes the headline gradient with a cool
       surprise, magenta runs as a deep tint under the surface. */
    cyan:         [ 84, 211, 254],   // sky anchor (gradient start)
    violet:       [130, 220, 250],
    magenta:      [ 80, 230, 180],   // mint anchor (gradient end)
    teal:         [ 39, 190, 180],   // teal anchor (secondary)
    plumDeep:     [255,   0, 158],   // magenta anchor (deep undertone)
    electric:     [150, 235, 255],
    purpleMid:    [100, 200, 230],
    ice:          [220, 245, 255],
    particleStar: [200, 235, 255],
    particleLine: [110, 200, 240],
    surfaceDeep0: [  3,   9,  18],
    surfaceDeep1: [  5,  13,  24],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  mariana: {
    name: 'Mariana',
    /* Abyssal — deepest blue-teal with the magenta anchor as a
       single bioluminescent flash at the gradient end. Teal +
       mint + sky carry the rest of the read so the dominant
       character stays blue-green. */
    cyan:         [ 25, 130, 155],   // deep teal-blue
    violet:       [ 50, 175, 195],
    magenta:      [255,   0, 158],   // magenta anchor (bioluminescent flash)
    teal:         [ 39, 190, 180],   // teal anchor (secondary)
    plumDeep:     [  4,  20,  30],
    electric:     [ 80, 230, 180],   // mint anchor (brightest highlight)
    purpleMid:    [ 60, 180, 195],
    ice:          [180, 230, 235],
    particleStar: [ 84, 211, 254],   // sky anchor (canvas stars)
    particleLine: [ 60, 180, 195],
    surfaceDeep0: [  2,   6,  10],
    surfaceDeep1: [  4,  10,  16],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  hotMint: {
    name: 'Hot Mint',
    cyan:         [ 39, 190, 180],
    violet:       [ 80, 220, 200],
    magenta:      [255,   0, 158],
    teal:         [ 80, 220, 210],
    plumDeep:     [ 60,   0,  50],
    electric:     [120, 235, 220],
    purpleMid:    [180, 100, 180],
    ice:          [200, 245, 230],
    particleStar: [180, 240, 220],
    particleLine: [ 80, 200, 200],
    surfaceDeep0: [  4,  10,  12],
    surfaceDeep1: [  7,  14,  18],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  razor: {
    name: 'Razor',
    cyan:         [255,   0, 158],
    violet:       [ 84, 211, 254],
    magenta:      [ 39, 190, 180],
    teal:         [255,  80, 180],
    plumDeep:     [ 60,   0,  40],
    electric:     [255, 100, 200],
    purpleMid:    [180, 130, 220],
    ice:          [255, 200, 230],
    particleStar: [255, 170, 220],
    particleLine: [220,  80, 180],
    surfaceDeep0: [ 12,   4,  12],
    surfaceDeep1: [ 18,   6,  18],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  frost: {
    name: 'Frost',
    cyan:         [ 84, 211, 254],
    violet:       [140, 230, 255],
    magenta:      [200, 245, 255],
    teal:         [150, 230, 250],
    plumDeep:     [ 10,  40,  80],
    electric:     [180, 240, 255],
    purpleMid:    [120, 220, 250],
    ice:          [220, 250, 255],
    particleStar: [200, 240, 255],
    particleLine: [120, 200, 240],
    surfaceDeep0: [  4,   9,  16],
    surfaceDeep1: [  7,  13,  22],
    dataUp:       [120, 240, 220],
    dataDown:     [255, 120, 130],
  },

  slateCyan: {
    name: 'Slate Cyan',
    cyan:         [ 60,  60,  60],
    violet:       [ 39, 190, 180],
    magenta:      [ 84, 211, 254],
    teal:         [ 80, 220, 210],
    plumDeep:     [ 20,  20,  20],
    electric:     [ 80,  80,  80],
    purpleMid:    [ 60, 180, 200],
    ice:          [200, 240, 250],
    particleStar: [150, 220, 240],
    particleLine: [ 60, 180, 220],
    surfaceDeep0: [  6,   6,   8],
    surfaceDeep1: [ 11,  11,  14],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  carbonPop: {
    name: 'Carbon Pop',
    cyan:         [ 60,  60,  60],
    violet:       [255,   0, 158],
    magenta:      [ 61, 214, 255],
    teal:         [ 80,  80,  80],
    plumDeep:     [ 10,  10,  10],
    electric:     [100, 100, 100],
    purpleMid:    [200,  80, 180],
    ice:          [240, 240, 240],
    particleStar: [255, 200, 230],
    particleLine: [200,  60, 160],
    surfaceDeep0: [  6,   6,   8],
    surfaceDeep1: [ 11,  11,  14],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  velvet: {
    name: 'Velvet',
    cyan:         [123,  97, 255],
    violet:       [180,  80, 220],
    magenta:      [255,   0, 158],
    teal:         [160, 100, 240],
    plumDeep:     [ 50,  10,  80],
    electric:     [200, 130, 255],
    purpleMid:    [220,  80, 220],
    ice:          [240, 200, 240],
    particleStar: [220, 170, 240],
    particleLine: [180, 100, 220],
    surfaceDeep0: [ 10,   6,  16],
    surfaceDeep1: [ 16,  10,  24],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  iris: {
    name: 'Iris',
    cyan:         [123,  97, 255],
    violet:       [ 84, 211, 254],
    magenta:      [ 39, 190, 180],
    teal:         [140, 130, 240],
    plumDeep:     [ 40,  30,  90],
    electric:     [160, 150, 250],
    purpleMid:    [120, 180, 240],
    ice:          [220, 240, 255],
    particleStar: [180, 200, 250],
    particleLine: [120, 150, 230],
    surfaceDeep0: [  6,   8,  18],
    surfaceDeep1: [ 10,  12,  24],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  helix: {
    name: 'Helix',
    cyan:         [ 61, 214, 255],
    violet:       [ 84, 211, 254],
    magenta:      [255,   0, 158],
    teal:         [100, 230, 240],
    plumDeep:     [ 80,   0,  60],
    electric:     [120, 235, 255],
    purpleMid:    [180, 130, 220],
    ice:          [200, 245, 255],
    particleStar: [200, 230, 255],
    particleLine: [100, 200, 240],
    surfaceDeep0: [  4,   9,  16],
    surfaceDeep1: [  7,  13,  22],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  seaGlass: {
    name: 'Sea Glass',
    cyan:         [ 39, 190, 180],
    violet:       [120, 220, 230],
    magenta:      [200, 245, 255],
    teal:         [100, 230, 220],
    plumDeep:     [ 20,  60,  90],
    electric:     [140, 235, 240],
    purpleMid:    [120, 200, 220],
    ice:          [220, 250, 255],
    particleStar: [180, 235, 245],
    particleLine: [100, 200, 220],
    surfaceDeep0: [  4,  10,  14],
    surfaceDeep1: [  7,  14,  20],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  /* ─── Mixed Palette — combines the pre-theming "original" colors
     (cyan 61/214/255, violet 123/97/255, magenta 168/85/247) with the
     official TI brand anchors (teal #27beb4, hot magenta #ff009e,
     coral #ff6357, sky #54d3fe). ─── */

  spectrum: {
    name: 'Spectrum',
    cyan:         [ 61, 214, 255],
    violet:       [123,  97, 255],
    magenta:      [255,   0, 158],
    teal:         [ 52, 211, 153],
    plumDeep:     [ 60,  20, 110],
    electric:     [ 90, 220, 255],
    purpleMid:    [160, 110, 240],
    ice:          [200, 240, 255],
    particleStar: [200, 200, 255],
    particleLine: [140, 110, 240],
    surfaceDeep0: [  6,   8,  16],
    surfaceDeep1: [ 10,  12,  22],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  bloom: {
    name: 'Bloom',
    cyan:         [ 39, 190, 180],
    violet:       [168,  85, 247],
    magenta:      [255,  99,  87],
    teal:         [ 80, 220, 200],
    plumDeep:     [ 80,  20,  60],
    electric:     [120, 235, 220],
    purpleMid:    [200, 110, 220],
    ice:          [255, 220, 220],
    particleStar: [240, 180, 220],
    particleLine: [200, 100, 200],
    surfaceDeep0: [  8,   6,  12],
    surfaceDeep1: [ 14,  10,  18],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  solstice: {
    name: 'Solstice',
    cyan:         [255,  99,  87],
    violet:       [ 61, 214, 255],
    magenta:      [168,  85, 247],
    teal:         [255, 130, 110],
    plumDeep:     [ 80,  20,  90],
    electric:     [255, 140, 120],
    purpleMid:    [180, 110, 230],
    ice:          [255, 220, 230],
    particleStar: [255, 180, 200],
    particleLine: [200, 100, 200],
    surfaceDeep0: [ 10,   6,  12],
    surfaceDeep1: [ 16,  10,  18],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255,  90,  80],
  },

  tropic: {
    name: 'Tropic',
    cyan:         [ 39, 190, 180],
    violet:       [ 84, 211, 254],
    magenta:      [168,  85, 247],
    teal:         [ 80, 220, 200],
    plumDeep:     [ 30,  40, 100],
    electric:     [120, 235, 220],
    purpleMid:    [120, 180, 240],
    ice:          [220, 245, 255],
    particleStar: [180, 230, 250],
    particleLine: [100, 200, 230],
    surfaceDeep0: [  4,   9,  16],
    surfaceDeep1: [  7,  13,  22],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  vortex: {
    name: 'Vortex',
    cyan:         [ 61, 214, 255],
    violet:       [168,  85, 247],
    magenta:      [255,   0, 158],
    teal:         [100, 230, 240],
    plumDeep:     [ 80,  10,  90],
    electric:     [120, 235, 255],
    purpleMid:    [210, 100, 220],
    ice:          [255, 200, 240],
    particleStar: [220, 180, 250],
    particleLine: [200,  80, 220],
    surfaceDeep0: [ 10,   6,  16],
    surfaceDeep1: [ 16,  10,  24],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  sunsetPier: {
    name: 'Sunset Pier',
    cyan:         [255,  99,  87],
    violet:       [255,   0, 158],
    magenta:      [123,  97, 255],
    teal:         [255, 130, 110],
    plumDeep:     [ 60,   0,  70],
    electric:     [255, 140, 130],
    purpleMid:    [220,  80, 180],
    ice:          [255, 210, 230],
    particleStar: [255, 170, 220],
    particleLine: [220,  80, 180],
    surfaceDeep0: [ 12,   5,  12],
    surfaceDeep1: [ 18,   8,  18],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255,  90,  80],
  },

  reefRun: {
    name: 'Reef Run',
    cyan:         [ 39, 190, 180],
    violet:       [ 61, 214, 255],
    magenta:      [255,  99,  87],
    teal:         [ 80, 220, 200],
    plumDeep:     [ 30,  30,  60],
    electric:     [120, 235, 220],
    purpleMid:    [120, 200, 240],
    ice:          [220, 245, 255],
    particleStar: [200, 230, 250],
    particleLine: [100, 200, 230],
    surfaceDeep0: [  4,  10,  14],
    surfaceDeep1: [  7,  14,  20],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255,  90,  80],
  },

  twilight: {
    name: 'Twilight',
    cyan:         [123,  97, 255],
    violet:       [168,  85, 247],
    magenta:      [ 61, 214, 255],
    teal:         [140, 110, 240],
    plumDeep:     [ 40,  20,  90],
    electric:     [160, 130, 250],
    purpleMid:    [180, 110, 240],
    ice:          [220, 230, 255],
    particleStar: [200, 200, 255],
    particleLine: [150, 120, 240],
    surfaceDeep0: [  8,   6,  18],
    surfaceDeep1: [ 12,  10,  26],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  watermelon: {
    name: 'Watermelon',
    cyan:         [ 39, 190, 180],
    violet:       [255,  99,  87],
    magenta:      [255,   0, 158],
    teal:         [ 80, 220, 200],
    plumDeep:     [ 80,   0,  40],
    electric:     [120, 235, 200],
    purpleMid:    [255,  90, 140],
    ice:          [255, 210, 220],
    particleStar: [255, 170, 200],
    particleLine: [240, 100, 140],
    surfaceDeep0: [  8,   8,  10],
    surfaceDeep1: [ 14,  12,  16],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255,  90,  80],
  },

  iridium: {
    name: 'Iridium',
    cyan:         [ 61, 214, 255],
    violet:       [ 84, 211, 254],
    magenta:      [ 39, 190, 180],
    teal:         [100, 230, 240],
    plumDeep:     [ 10,  40,  80],
    electric:     [120, 235, 255],
    purpleMid:    [ 80, 200, 230],
    ice:          [200, 245, 255],
    particleStar: [180, 235, 250],
    particleLine: [ 90, 200, 240],
    surfaceDeep0: [  3,   9,  16],
    surfaceDeep1: [  5,  13,  22],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  confetti: {
    name: 'Confetti',
    cyan:         [255,   0, 158],
    violet:       [168,  85, 247],
    magenta:      [ 84, 211, 254],
    teal:         [255,  90, 180],
    plumDeep:     [ 60,  10,  80],
    electric:     [255, 100, 200],
    purpleMid:    [180, 110, 230],
    ice:          [220, 240, 255],
    particleStar: [200, 200, 255],
    particleLine: [200,  80, 220],
    surfaceDeep0: [ 10,   6,  16],
    surfaceDeep1: [ 16,  10,  22],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  mirage: {
    name: 'Mirage',
    cyan:         [255,  99,  87],
    violet:       [123,  97, 255],
    magenta:      [ 39, 190, 180],
    teal:         [255, 130, 110],
    plumDeep:     [ 40,  20,  80],
    electric:     [255, 140, 120],
    purpleMid:    [140, 120, 240],
    ice:          [220, 230, 255],
    particleStar: [220, 200, 240],
    particleLine: [150, 120, 230],
    surfaceDeep0: [ 10,   8,  16],
    surfaceDeep1: [ 16,  12,  22],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255,  90,  80],
  },

  /* ─── Top Picks — 3 focused variants + 7 cohesive themes leaning into
     TI greens/teals and dark-gray (iridium / slate-cyan) territory.
     Designed as the curated "house" set. ─── */

  verdantCore: {
    name: 'Verdant Core',
    cyan:         [ 39, 190, 180],
    violet:       [ 80, 220, 180],
    magenta:      [120, 235, 200],
    teal:         [ 60, 210, 195],
    plumDeep:     [  5,  40,  50],
    electric:     [110, 240, 210],
    purpleMid:    [ 70, 200, 180],
    ice:          [200, 250, 230],
    particleStar: [180, 240, 220],
    particleLine: [ 80, 210, 190],
    surfaceDeep0: [  3,  10,  10],
    surfaceDeep1: [  6,  14,  16],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  slateVerde: {
    name: 'Slate Verde',
    cyan:         [ 60,  60,  60],
    violet:       [ 39, 190, 180],
    magenta:      [ 80, 230, 180],
    teal:         [ 80,  80,  80],
    plumDeep:     [ 12,  16,  18],
    electric:     [110, 235, 200],
    purpleMid:    [ 60, 180, 170],
    ice:          [200, 240, 230],
    particleStar: [150, 220, 200],
    particleLine: [ 60, 180, 170],
    surfaceDeep0: [  6,   8,   8],
    surfaceDeep1: [ 11,  13,  14],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  mintIridium: {
    name: 'Mint Iridium',
    cyan:         [ 80, 230, 200],
    violet:       [ 39, 190, 180],
    magenta:      [120, 220, 230],
    teal:         [100, 230, 210],
    plumDeep:     [ 10,  40,  50],
    electric:     [120, 240, 220],
    purpleMid:    [ 80, 200, 210],
    ice:          [220, 250, 250],
    particleStar: [190, 240, 235],
    particleLine: [100, 215, 215],
    surfaceDeep0: [  3,   9,  12],
    surfaceDeep1: [  6,  13,  18],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  forestTide: {
    name: 'Forest Tide',
    cyan:         [ 50, 180, 140],
    violet:       [ 39, 190, 180],
    magenta:      [ 84, 211, 254],
    teal:         [ 70, 210, 175],
    plumDeep:     [  6,  30,  35],
    electric:     [ 90, 225, 195],
    purpleMid:    [ 70, 195, 200],
    ice:          [200, 245, 235],
    particleStar: [170, 230, 215],
    particleLine: [ 70, 195, 195],
    surfaceDeep0: [  3,  10,  12],
    surfaceDeep1: [  6,  14,  18],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  carbonTide: {
    name: 'Carbon Tide',
    cyan:         [ 50,  55,  60],
    violet:       [ 39, 190, 180],
    magenta:      [ 84, 211, 254],
    teal:         [ 70,  75,  80],
    plumDeep:     [ 10,  12,  14],
    electric:     [110, 235, 220],
    purpleMid:    [ 70, 200, 215],
    ice:          [210, 245, 250],
    particleStar: [165, 230, 235],
    particleLine: [ 80, 205, 220],
    surfaceDeep0: [  6,   7,   9],
    surfaceDeep1: [ 11,  12,  15],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  /* Mixed-palette accent — brand teal core, mixed-palette purple, sky finish. */
  reefSpectrum: {
    name: 'Reef Spectrum',
    cyan:         [ 39, 190, 180],
    violet:       [168,  85, 247],
    magenta:      [ 84, 211, 254],
    teal:         [ 80, 220, 200],
    plumDeep:     [ 30,  20,  80],
    electric:     [110, 230, 220],
    purpleMid:    [180, 120, 230],
    ice:          [220, 240, 255],
    particleStar: [200, 215, 250],
    particleLine: [150, 110, 230],
    surfaceDeep0: [  4,   8,  16],
    surfaceDeep1: [  8,  12,  22],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  /* Mint primary, brand teal middle, deep plum/magenta close from mixed palette. */
  mintPlum: {
    name: 'Mint Plum',
    cyan:         [ 80, 220, 180],
    violet:       [ 39, 190, 180],
    magenta:      [168,  85, 247],
    teal:         [100, 230, 200],
    plumDeep:     [ 60,  20,  90],
    electric:     [120, 235, 200],
    purpleMid:    [180, 110, 230],
    ice:          [220, 245, 240],
    particleStar: [200, 220, 250],
    particleLine: [140, 110, 220],
    surfaceDeep0: [  6,   8,  14],
    surfaceDeep1: [ 10,  13,  20],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  /* Slate gray graphite base + bloom magenta + sky highlight. */
  graphiteBloom: {
    name: 'Graphite Bloom',
    cyan:         [ 70,  75,  85],
    violet:       [168,  85, 247],
    magenta:      [ 84, 211, 254],
    teal:         [ 90, 100, 110],
    plumDeep:     [ 20,  15,  35],
    electric:     [180, 130, 240],
    purpleMid:    [190, 130, 240],
    ice:          [220, 230, 250],
    particleStar: [220, 200, 250],
    particleLine: [160, 110, 230],
    surfaceDeep0: [  6,   7,  10],
    surfaceDeep1: [ 11,  12,  16],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  /* Violet-forward #1 — teal/green core, original violet (123,97,255) accent. */
  verdantIris: {
    name: 'Verdant Iris',
    cyan:         [ 60, 210, 180],
    violet:       [123,  97, 255],
    magenta:      [ 84, 211, 254],
    teal:         [ 80, 220, 200],
    plumDeep:     [ 40,  20,  90],
    electric:     [100, 230, 210],
    purpleMid:    [150, 110, 240],
    ice:          [215, 235, 250],
    particleStar: [200, 220, 255],
    particleLine: [130, 110, 230],
    surfaceDeep0: [  6,   8,  16],
    surfaceDeep1: [ 10,  13,  22],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

  /* Violet-forward #2 — slate gray base, original violet primary. */
  slateTwilight: {
    name: 'Slate Twilight',
    cyan:         [ 60,  60,  60],
    violet:       [123,  97, 255],
    magenta:      [ 84, 211, 254],
    teal:         [ 80,  80,  90],
    plumDeep:     [ 14,  10,  24],
    electric:     [140, 110, 240],
    purpleMid:    [180, 130, 240],
    ice:          [220, 220, 250],
    particleStar: [200, 200, 250],
    particleLine: [140, 110, 230],
    surfaceDeep0: [  6,   6,  10],
    surfaceDeep1: [ 11,  10,  16],
    dataUp:       [ 80, 230, 180],
    dataDown:     [255, 100, 110],
  },

};

/* Alias so existing code that referenced `BRAND_PALETTES.default` keeps working. */
BRAND_PALETTES.default = BRAND_PALETTES.original;

/**
 * Grouped layout for the dev theme picker. Each group renders as an
 * <optgroup> in the dropdown. Order is preserved.
 */
export const PALETTE_GROUPS = [
  {
    label: 'Default',
    keys: ['original'],
  },
  {
    label: 'Top Picks',
    keys: [
      /* Teal / green / slate cores */
      'verdantCore', 'slateVerde', 'mintIridium', 'forestTide', 'carbonTide',
      /* Mixed-palette purple (168,85,247) accents */
      'reefSpectrum', 'mintPlum', 'graphiteBloom',
      /* Original violet (123,97,255) — two violet-forward picks */
      'verdantIris', 'slateTwilight',
    ],
  },
  {
    label: 'TI Palette',
    keys: [
      'tealSignal', 'skyForward', 'magentaPulse', 'coralHeat',
      'tide', 'neonBloom', 'cyberSunset', 'aurora', 'reef', 'plasma',
      'lagoon', 'lagoon2', 'meridian', 'ebbtide', 'tidepool', 'hotMint', 'razor', 'frost',
      'slateCyan', 'carbonPop', 'velvet', 'iris', 'helix', 'seaGlass',
    ],
  },
  {
    label: 'Blue-Greens',
    keys: [
      /* Lagoon-family, no coral — a cool blue-green spectrum from
         glacier-bright to abyssal. */
      'glacier', 'shoal', 'deepReef', 'kelpForest', 'mariana',
    ],
  },
  {
    label: 'Mixed Palette',
    keys: [
      'spectrum', 'bloom', 'solstice', 'tropic',
      'vortex', 'sunsetPier', 'reefRun', 'twilight',
      'watermelon', 'iridium', 'confetti', 'mirage',
    ],
  },
];

/** @type {(keyof typeof BRAND_PALETTES)[]} */
export const PALETTE_KEYS = /** @type {any} */ (
  Object.keys(BRAND_PALETTES).filter(k => k !== 'default')
);

/**
 * Maps BrandPalette keys to CSS custom property names on :root.
 * Canvas-only keys (particleStar, particleLine, surfaceDeep0/1, dataUp, dataDown)
 * are omitted — they have no CSS var equivalent.
 */
export const PALETTE_CSS_VAR_MAP = {
  cyan:      '--ti-rgb-brand-cyan',
  violet:    '--ti-rgb-brand-violet',
  magenta:   '--ti-rgb-brand-magenta',
  teal:      '--ti-rgb-brand-teal',
  plumDeep:  '--ti-rgb-brand-plum-deep',
  electric:  '--ti-rgb-brand-electric',
  purpleMid: '--ti-rgb-brand-purple-mid',
  ice:       '--ti-rgb-brand-ice',
};
